import { FC } from "react";

export type BackgroundVariant = "gradient" | "solid" | "transparent";

export interface ThemeBackgroundProps {
  /**
   * Background style behind the logo watermark.
   * - gradient: subtle theme-based gradient (light/dark friendly)
   * - solid: very subtle solid fill using --muted
   * - transparent: no fill, only the logo watermark
   */
  variant?: BackgroundVariant;
  /**
   * Logo watermark opacity (kept for backwards compatibility).
   */
  logoOpacity?: number;
  /**
   * Logo size as a proportion of viewport (kept for backwards compatibility).
   */
  logoScale?: number;
  /** Optional custom background image URL (defaults to /flowstate-background.jpg) */
  bgImageSrc?: string;
  /** Optional extra class for the container. */
  className?: string;
}

/**
 * Full-viewport theme-aware background with Flowstate cosmic vortex.
 * Features the default Flowstate cosmic vortex background image in full true color.
 * Adapts to light/dark via CSS variables (--primary, --muted, --background).
 */
export const ThemeBackground: FC<ThemeBackgroundProps> = ({
  variant = "gradient",
  bgImageSrc = "/flowstate-background.jpg",
  className = "",
}) => {
  return (
    <div
      className={`theme-background pointer-events-none fixed inset-0 z-0 isolate ${className}`}
      aria-hidden
    >
      {/* Layer 1: default Flowstate cosmic vortex background image in full true color */}
      {variant !== "transparent" && (
        <div
          className="theme-background-image absolute inset-0 bg-cover bg-center transition-opacity duration-700 pointer-events-none"
          style={{
            backgroundImage: `url("${bgImageSrc}")`,
            opacity: 1.0,
          }}
        />
      )}

      {/* Layer 1.5: subtle neutral edge vignette for depth without theme-color tinting */}
      {variant !== "transparent" && (
        <div
          className="theme-background-vignette absolute inset-0 pointer-events-none"
          style={{
            background:
              variant === "solid"
                ? "rgba(0, 0, 0, 0.25)"
                : "radial-gradient(ellipse at center, transparent 55%, rgba(0, 0, 0, 0.45) 100%)",
          }}
        />
      )}
    </div>
  );
};

export default ThemeBackground;
